/**
 * Carbon Compactors — custom PostHog events
 *
 * Fires two conversion-adjacent events on top of PostHog's autocapture:
 *   - contact_form_submitted   (any contact form, with attribution — sent from the
 *                               ?contact_posted=true page, timestamped at submit)
 *   - added_to_cart            (intercepts fetch() calls to /cart/add)
 *
 * Depends on window.posthog (loaded from theme.liquid <head>)
 * and optionally window.CCAttribution (cc-attribution.js) for gclid/utm props.
 */
(function () {
  'use strict';

  function getAttr() {
    return (window.CCAttribution && window.CCAttribution.getAll)
      ? window.CCAttribution.getAll()
      : {};
  }

  function attrProps() {
    var a = getAttr();
    return {
      gclid: a.gclid || null,
      gbraid: a.gbraid || null,
      wbraid: a.wbraid || null,
      msclkid: a.msclkid || null,
      fbclid: a.fbclid || null,
      gad_source: a.gad_source || null,
      gad_campaignid: a.gad_campaignid || null,
      srsltid: a.srsltid || null,
      mc_feed: a.mc_feed || null,
      mc_item: a.mc_item || null,
      gp_kw: a.gp_kw || null,
      gp_ad: a.gp_ad || null,
      gp_mt: a.gp_mt || null,
      gp_cid: a.gp_cid || null,
      gp_agid: a.gp_agid || null,
      gp_dev: a.gp_dev || null,
      gp_pid: a.gp_pid || null,
      utm_source: a.utm_source || null,
      utm_medium: a.utm_medium || null,
      utm_campaign: a.utm_campaign || null,
      utm_term: a.utm_term || null,
      utm_content: a.utm_content || null,
      referrer_domain: a.referrer_domain || null,
      landing_page: a.landing_page || null,
      first_seen: a.first_seen || null
    };
  }

  // 1) Contact form submit — wire every Shopify contact form on the page
  //    (PDP quote form, contact page, PDF capture, CTA capture, collection
  //    brochure, etc.). We detect them by presence of a `contact[email]`
  //    input, which is Shopify's canonical name for the contact form.
  //    Identifies the visitor in PostHog by email on submit AND on email
  //    blur, so abandoned-but-emailed sessions still get tied to a person.
  function wireContactForms() {
    var emailInputs = document.querySelectorAll('input[name="contact[email]"]');
    for (var i = 0; i < emailInputs.length; i++) {
      var form = emailInputs[i].form || emailInputs[i].closest('form');
      if (!form || form.dataset.posthogWired === '1') continue;
      // Newsletter blocks use {% form 'customer' %} but still name their input
      // contact[email]. Those are marketing opt-ins, not leads — counting them
      // would inflate lead volume. Shopify stamps the kind in form_type.
      var kind = form.querySelector('[name="form_type"]');
      if (kind && kind.value !== 'contact') continue;
      form.dataset.posthogWired = '1';
      wireOneContactForm(form);
    }
  }

  function val(form, field) {
    var el = form.querySelector('[name="contact[' + field + ']"]');
    return el && el.value ? el.value.trim() : '';
  }

  function wireOneContactForm(form) {
    var emailEl = form.querySelector('[name="contact[email]"]');
    // Person name comes ONLY from real name fields. contact[name] used to hold a
    // canned label ("PDF Download Request") on capture forms, which this script
    // then wrote into the person's PostHog name. That label is contact[request_type]
    // now — never read it here. See ops/form-schema.md.
    var firstEl = form.querySelector('[name="contact[first_name]"]');
    var lastEl  = form.querySelector('[name="contact[last_name]"]');
    // Some forms (the baler picker) collect a single visible full-name field.
    // That is a real person; a HIDDEN contact[name] is a legacy canned label and
    // must never be treated as one.
    var fullNameEl = form.querySelector('[name="contact[name]"]');
    if (fullNameEl && fullNameEl.type === 'hidden') fullNameEl = null;
    var phoneEl = form.querySelector('[name="contact[phone]"]');
    var formId  = form.id || 'contact_form';

    var lastIdentified = '';
    function identifyFromEmail() {
      if (!window.posthog || !window.posthog.identify) return;
      if (!emailEl) return;
      var email = (emailEl.value || '').trim().toLowerCase();
      if (!email || email === lastIdentified) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      lastIdentified = email;
      try {
        var props = { email: email };
        var full = ((firstEl && firstEl.value ? firstEl.value : '') + ' ' +
                    (lastEl && lastEl.value ? lastEl.value : '')).trim();
        if (!full && fullNameEl && fullNameEl.value) full = fullNameEl.value.trim();
        if (full) props.name = full;
        if (phoneEl && phoneEl.value) props.phone = phoneEl.value;
        window.posthog.identify(email, props);
      } catch (_) { /* no-op */ }
    }

    if (emailEl) {
      emailEl.addEventListener('blur', identifyFromEmail);
      emailEl.addEventListener('change', identifyFromEmail);
    }

    form.addEventListener('submit', function () {
      if (!window.posthog) return;
      identifyFromEmail();
      var props = attrProps();
      props.form_page = window.location.pathname;
      props.form_id = formId;
      // Non-PII semantics, so PostHog can still say WHAT was asked for when the
      // Shopify notification email is lost. Event props never carry identifying
      // values (email/name/phone/body); those go on the PERSON via $set below.
      props.intent = val(form, 'intent') || null;
      props.product_inquiry = val(form, 'product_inquiry') || null;
      props.product_handle = val(form, 'product_handle') || null;
      props.has_name  = !!((firstEl && firstEl.value) || (lastEl && lastEl.value) ||
                           (fullNameEl && fullNameEl.value));
      props.has_email = !!(emailEl && emailEl.value);
      props.has_phone = !!(phoneEl && phoneEl.value);
      // Person properties at SUBMIT time. identifyFromEmail() runs on email blur,
      // usually before the phone is typed, and then skips on submit because the
      // email is unchanged — so phone never reached PostHog. $set on the submit
      // event is the reliable path; the ClickUp CRM destination reads it.
      var set = {};
      var full = ((firstEl && firstEl.value ? firstEl.value : '') + ' ' +
                  (lastEl && lastEl.value ? lastEl.value : '')).trim();
      if (!full && fullNameEl && fullNameEl.value) full = fullNameEl.value.trim();
      if (emailEl && emailEl.value) set.email = emailEl.value.trim().toLowerCase();
      if (full) set.name = full;
      if (phoneEl && phoneEl.value) set.phone = phoneEl.value.trim();
      if (val(form, 'zip')) set.zip = val(form, 'zip');
      if (val(form, 'company')) set.company = val(form, 'company');
      props.$set = set;
      // Fired from the confirmation page, not here: the POST navigates away at
      // once and Firefox/Edge dropped the in-flight event (Jul 18, Oct 1 — the
      // collection brochure form, Chrome 5/5 fine). Shopify's redirect to
      // ?contact_posted=true is also proof the submission was accepted.
      var payload = { formId: formId, props: props, email: set.email || '', ts: Date.now() };
      if (!stashLead(payload)) {
        // No sessionStorage: send now, by beacon, which survives the navigation.
        window.posthog.capture('contact_form_submitted', props, { transport: 'sendBeacon' });
      }
    });
  }

  var LEAD_KEY = 'cc_pending_lead_v1';

  function stashLead(payload) {
    try {
      sessionStorage.setItem(LEAD_KEY, JSON.stringify(payload));
      return sessionStorage.getItem(LEAD_KEY) !== null;
    } catch (_) { return false; }
  }

  // On the page Shopify redirects to after a successful post, send the lead
  // stashed at submit. The hash is the posting form's id (see cc-form-success.js);
  // a pending lead for a different form stays put. Removed before capture, so a
  // reload of the confirmation URL never double-counts.
  function flushPostedLead() {
    var p;
    try { p = JSON.parse(sessionStorage.getItem(LEAD_KEY)); } catch (_) { return; }
    if (!p || !p.formId || !p.props) return;
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (_) { return; }
    if (params.get('contact_posted') !== 'true') return;
    var hash = (window.location.hash || '').replace(/^#/, '');
    if (hash && hash !== p.formId) return;
    try { sessionStorage.removeItem(LEAD_KEY); } catch (_) {}
    if (!window.posthog) return;
    try {
      if (p.email && window.posthog.identify) window.posthog.identify(p.email);
      window.posthog.capture('contact_form_submitted', p.props, { timestamp: new Date(p.ts) });
    } catch (_) { /* no-op */ }
  }

  // 2) Add to cart — intercept fetch() calls to /cart/add
  function wireCartInterceptor() {
    if (!window.fetch || window.__ccPosthogCartWired) return;
    window.__ccPosthogCartWired = true;
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
      var promise = origFetch.apply(this, args);
      if (url && url.indexOf('/cart/add') !== -1 && window.posthog) {
        promise.then(function (res) {
          // Only capture 2xx — avoid duplicating for failed adds
          if (!res || !res.ok) return null;
          return res.clone().json();
        }).then(function (data) {
          if (!data) return;
          // Shopify returns the added item payload (shape depends on ajax_api)
          var item = (data.items && data.items[0]) || data;
          var props = attrProps();
          props.variant_id = item.id || item.variant_id || null;
          props.product_id = item.product_id || null;
          props.product_title = item.product_title || item.title || null;
          props.variant_title = item.variant_title || null;
          props.price = typeof item.price === 'number' ? item.price / 100 : null;
          props.quantity = item.quantity || 1;
          props.sku = item.sku || null;
          window.posthog.capture('added_to_cart', props);
        }).catch(function () { /* swallow */ });
      }
      return promise;
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      flushPostedLead();
      wireContactForms();
      wireCartInterceptor();
    });
  } else {
    flushPostedLead();
    wireContactForms();
    wireCartInterceptor();
  }
})();
